import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAccount } from 'wagmi';
import AudioRecorder from './AudioRecorder';
import NGOSelector from './NGOSelector';
import ProgressBar from './ProgressBar';
import { getApiBaseUrl } from '../utils/api';
import { testimonyEvents } from '../utils/analytics';
import { debugLog, debugError } from '../utils/debug';
import './TestimonyForm.css';

const ConfessionForm = ({ onFocusChange }) => {
  const { address } = useAccount();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    textContent: '',
    selectedNGOs: [],
    audioBlob: null,
    audioDuration: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ngos, setNGOs] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [formVisible, setFormVisible] = useState(true);
  const [audioElement, setAudioElement] = useState(null);
  const [showRecordingPanel, setShowRecordingPanel] = useState(true);
  const [isFocused, setIsFocused] = useState(false);

  // Handle focus change
  useEffect(() => {
    if (onFocusChange) {
      onFocusChange(isFocused);
    }
    
    // ALWAYS remove existing handler first to prevent duplicates
    if (window.scrollConstraintHandler) {
      window.removeEventListener('scroll', window.scrollConstraintHandler);
      window.scrollConstraintHandler = null;
      debugLog('useEffect: Removed existing scroll handler');
    }
    
    // Constrain scrolling to testimony form section with more lenient boundaries
    if (isFocused) {
      // Add scroll constraint immediately (no auto-scrolling)
      const formSection = document.getElementById('submit');
      if (formSection) {
        const handleScroll = (e) => {
          const formSectionTop = formSection.offsetTop;
          const formSectionBottom = formSectionTop + formSection.offsetHeight;
          const currentScrollY = window.scrollY;
          
          // Allow generous scrolling boundaries for steps 1-2
          // Constrain if scrolling too far above the form
          if (currentScrollY < formSectionTop - 100) {
            window.scrollTo({
              top: formSectionTop - 100,
              behavior: 'smooth'
            });
          } 
          // Constrain if scrolling too far below
          else if (currentScrollY + window.innerHeight > formSectionBottom + 200) {
            window.scrollTo({
              top: formSectionBottom + 200 - window.innerHeight,
              behavior: 'smooth'
            });
          }
        };
        
        // Store the handler so we can remove it later
        window.scrollConstraintHandler = handleScroll;
        window.addEventListener('scroll', handleScroll, { passive: true });
      }
    } else {
      // Remove scroll constraint
      debugLog('useEffect: removing scroll handler, isFocused=', isFocused, 'handler exists:', !!window.scrollConstraintHandler);
      if (window.scrollConstraintHandler) {
        window.removeEventListener('scroll', window.scrollConstraintHandler);
        window.scrollConstraintHandler = null;
        debugLog('useEffect: scroll handler removed');
      }
    }
  }, [isFocused, onFocusChange, step]);

  const handleTextareaFocus = () => {
    setIsFocused(true);
    testimonyEvents.formFocused();
  };

  const handleCancelFocus = () => {
    debugLog('Cancel focus called from step:', step, 'removing scroll handler:', !!window.scrollConstraintHandler);
    
    // Debug: Check body overflow styles
    debugLog('Body overflow before cancel:', document.body.style.overflow);
    debugLog('HTML overflow before cancel:', document.documentElement.style.overflow);
    
    // Immediately remove scroll constraint before state update
    if (window.scrollConstraintHandler) {
      window.removeEventListener('scroll', window.scrollConstraintHandler);
      window.scrollConstraintHandler = null;
      debugLog('Scroll handler removed');
    }
    
    // Make sure body/html can scroll
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    debugLog('Body/HTML overflow cleared');
    
    // Track cancel event
    testimonyEvents.formCancelled(step);
    
    setIsFocused(false);
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Ensure scroll constraint is removed if component unmounts while focused
      if (window.scrollConstraintHandler) {
        window.removeEventListener('scroll', window.scrollConstraintHandler);
        window.scrollConstraintHandler = null;
      }
    };
  }, []);

  useEffect(() => {
    fetchNGOs();
    
    return () => {
      // Cleanup function
    };
  }, []);

  const fetchNGOs = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/ngos`);
      const data = await response.json();
      setNGOs(data);
    } catch (error) {
      console.error('Error fetching charities:', error);
      toast.error('Failed to load charities');
    }
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= 250) {
      setFormData(prev => ({
        ...prev,
        textContent: value
      }));
    }
  };

  const handleAudioRecorded = (audioBlob, duration) => {
    setFormData(prev => ({
      ...prev,
      audioBlob,
      audioDuration: duration
    }));
  };

  const handleNGOSelection = (selectedIds) => {
    setFormData(prev => ({
      ...prev,
      selectedNGOs: selectedIds
    }));
  };

  const canProceedToStep = (stepNum) => {
    switch (stepNum) {
      case 2:
        return formData.textContent.trim().length > 0;
      case 3:
        return formData.audioBlob !== null;
      default:
        return true;
    }
  };

  const canSubmit = () => {
    return formData.textContent.trim().length > 0 && 
           formData.audioBlob !== null && 
           formData.selectedNGOs.length === 3 &&
           termsAccepted;
  };

  const smoothScrollTo = (element, position = 'start', duration = 500) => {
    if (!element) return;
    
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top + window.pageYOffset;
    
    let targetY;
    if (position === 'center') {
      targetY = elementTop - (window.innerHeight / 2) + (elementRect.height / 2);
    } else {
      targetY = elementTop - 20; // 20px offset from top
    }
    
    const startY = window.pageYOffset;
    const distance = targetY - startY;
    let startTime = null;
    
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    
    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      const ease = easeOutCubic(progress);
      window.scrollTo(0, startY + (distance * ease));
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };
    
    requestAnimationFrame(animation);
  };

  const scrollToSteps = () => {
    const stepsElement = document.querySelector('.steps-indicator');
    if (stepsElement) {
      smoothScrollTo(stepsElement, 'start', 500);
    }
  };

  const scrollToProgress = () => {
    const progressElement = document.querySelector('.progress-bar') || document.querySelector('.steps-indicator');
    if (progressElement) {
      smoothScrollTo(progressElement, 'center', 500);
    }
  };

  const handleStepTransition = (nextStep) => {
    // Track step progression
    if (nextStep > step) {
      testimonyEvents.stepCompleted(step);
      testimonyEvents.stepStarted(nextStep);
    } else {
      testimonyEvents.stepBack(step, nextStep);
    }
    
    if (nextStep === 2) {
      setShowRecordingPanel(true);
    }
    setStep(nextStep);
    // Keep focused state active when moving between steps
    // (don't change isFocused here - only cancel button should deactivate it)
    
    scrollToSteps();
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      toast.error('Please complete all steps');
      return;
    }

    // Track submission start
    testimonyEvents.submissionStarted({
      textLength: formData.textContent.length,
      audioDuration: formData.audioDuration,
      ngoCount: formData.selectedNGOs.length
    });

    // Scroll to progress area first
    scrollToProgress();

    setIsSubmitting(true);
    setShowProgress(true);
    setProgress(0);

    // Simulate progress animation to 100% over 5 seconds
    const simulateProgress = () => {
      const stages = [
        { progress: 0, stage: { label: 'Starting', description: 'Initializing confession creation', icon: '🚀' }, delay: 500 },
        { progress: 25, stage: { label: 'Processing', description: 'Preparing your confession', icon: '💾' }, delay: 1200 },
        { progress: 60, stage: { label: 'Uploading', description: 'Saving to IPFS and blockchain', icon: '🪙' }, delay: 1500 },
        { progress: 85, stage: { label: 'Finalizing', description: 'Creating token and metadata', icon: '⚡' }, delay: 1000 },
        { progress: 100, stage: { label: 'Complete', description: 'Your confession has been submitted successfully!', icon: '🎉' }, delay: 800 }
      ];

      let currentStageIndex = 0;
      
      const progressInterval = () => {
        if (currentStageIndex < stages.length) {
          const currentStageData = stages[currentStageIndex];
          setProgress(currentStageData.progress);
          setCurrentStage(currentStageData.stage);
          
          // When we reach 100%, show success and reset form
          if (currentStageData.progress === 100) {
            setTimeout(() => {
              // Show longer-lasting success message with indexing info
              toast.success('Confession submitted successfully! 🎉\n\nYour confession is being processed and may take a few minutes to appear in the gallery and become fully indexed.', {
                duration: 8000, // Show for 8 seconds
                style: {
                  maxWidth: '500px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }
              });
              
              // Reset form state and disable focused mode
              setFormData({
                textContent: '',
                selectedNGOs: [],
                audioBlob: null,
                audioDuration: 0
              });
              setTermsAccepted(false);
              setStep(1);
              setIsSubmitting(false);
              setShowProgress(false);
              
              // Clean up focused mode and scroll constraints
              setIsFocused(false);
              
              // Ensure body/html can scroll properly after reset
              document.body.style.overflow = '';
              document.documentElement.style.overflow = '';
              
              // Remove any remaining scroll handlers
              if (window.scrollConstraintHandler) {
                window.removeEventListener('scroll', window.scrollConstraintHandler);
                window.scrollConstraintHandler = null;
              }
              
              // Track successful submission (UI completion)
              testimonyEvents.submissionCompleted('ui-completed');
            }, currentStageData.delay);
          } else {
            setTimeout(progressInterval, currentStageData.delay);
          }
          currentStageIndex++;
        }
      };
      
      progressInterval();
    };

    // Start UI progress simulation (fast)
    simulateProgress();

    // Submit to backend in the background (don't await)
    submitToBackgroundAsync(formData);
  };

  const submitToBackgroundAsync = async (submissionData) => {
    try {
      // First, create charity combination
      const combinationResponse = await fetch(`${getApiBaseUrl()}/combinations/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ngoIds: submissionData.selectedNGOs
        })
      });

      if (!combinationResponse.ok) {
        throw new Error('Failed to create charity combination');
      }

      const combination = await combinationResponse.json();

      // Submit confession
      const formDataToSend = new FormData();
      formDataToSend.append('textContent', submissionData.textContent);
      formDataToSend.append('ngoCombinationId', combination._id);
      formDataToSend.append('audioDuration', Math.round(submissionData.audioDuration));
      
      // Convert webm blob to file
      const audioFile = new File([submissionData.audioBlob], 'confession.webm', { 
        type: 'audio/webm' 
      });
      formDataToSend.append('audio', audioFile);

      // Submit the actual request
      const response = await fetch(`${getApiBaseUrl()}/testimonies`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Failed to submit confession');
      }

      const confession = await response.json();
      debugLog('Background submission completed:', confession);

      // Track successful backend submission
      testimonyEvents.submissionCompleted(confession.id || confession._id);

    } catch (error) {
      debugError('Background submission failed:', error);
      
      // Track submission failure
      testimonyEvents.submissionFailed(error);
      
      // Don't show error to user since they already saw success
      // Could optionally log to monitoring service here
    }
  };

  const steps = [
    { title: 'Write', description: 'Share your story' },
    { title: 'Record', description: 'Add your voice' },
    { title: 'Submit', description: 'Select Charities' }
  ];

  const progressStages = [
    { label: 'Starting', description: 'Initializing confession creation', icon: '🚀', minProgress: 0, maxProgress: 10 },
    { label: 'Processing', description: 'Preparing your confession', icon: '💾', minProgress: 10, maxProgress: 40 },
    { label: 'Uploading', description: 'Saving to IPFS and blockchain', icon: '🪙', minProgress: 40, maxProgress: 75 },
    { label: 'Finalizing', description: 'Creating token and metadata', icon: '⚡', minProgress: 75, maxProgress: 95 },
    { label: 'Complete', description: 'Your confession has been submitted successfully', icon: '🎉', minProgress: 95, maxProgress: 100 }
  ];

  return (
    <section id="submit" className="testimony-form-section section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Share Your Confession</h2>
          <p>Turn your story into a crypto token that fuels charity.</p>
        </motion.div>

        <div className="form-container">

          {/* Show Progress Bar when submitting */}
          {showProgress && (
            <ProgressBar 
              progress={progress} 
              stages={progressStages}
            />
          )}
          
          {/* Progress Steps - hide when showing progress */}
          {!showProgress && (
            <motion.div 
              className="steps-indicator"
              initial={{ opacity: 0, y: -10 }}
              animate={{ 
                opacity: formVisible ? 1 : 0,
                y: formVisible ? 0 : -10
              }}
              transition={{ 
                duration: 0.6,
                delay: formVisible ? 0.2 : 0,
                ease: "easeOut"
              }}
            >
              {steps.map((stepInfo, index) => (
                <div
                  key={index}
                  className={`step ${step > index + 1 ? 'completed' : step === index + 1 ? 'active' : ''}`}
                >
                  <div className="step-number">
                    {step > index + 1 ? <CheckCircle size={20} /> : index + 1}
                  </div>
                  <div className="step-info">
                    <span className="step-title">{stepInfo.title}</span>
                    <span className="step-description">{stepInfo.description}</span>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* AudioRecorder - positioned after steps for proper accessibility */}
          {step === 2 && !showProgress && showRecordingPanel && (
            <motion.div 
              className="audio-recorder-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <div className="recorder-content">
                <h3>🎤 Add Your Voice</h3>
                <p>Record up to 60 seconds of audio to bring your confession to life. A short preview will be public; the full audio is for token owners only.</p>
                
                <AudioRecorder 
                  key="audio-recorder-persistent" 
                  onAudioRecorded={handleAudioRecorded}
                  maxDuration={60}
                  initialBlob={formData.audioBlob}
                  initialDuration={formData.audioDuration}
                />
                
                <div className="recording-tips">
                  <h4>🎯 Tips:</h4>
                  <ul>
                    <li><strong>Find a quiet space</strong></li>
                    <li><strong>Speak clearly and naturally</strong></li>
                    <li><strong>Keep it real</strong> — imperfection is human</li>
                    <li><strong>Stay within 60 seconds</strong></li>
                  </ul>
                </div>
                
                <div className="step-actions">
                  {isFocused && (
                    <motion.button
                      className="btn btn-secondary cancel-focus-btn"
                      onClick={handleCancelFocus}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <X size={16} />
                      Cancel
                    </motion.button>
                  )}
                  <motion.button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowRecordingPanel(false);
                      handleStepTransition(1);
                      // Keep focused state when going back
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Back
                  </motion.button>
                  <motion.button
                    className="btn btn-primary"
                    disabled={!canProceedToStep(3)}
                    onClick={() => handleStepTransition(3)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Continue to Submit
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Form Content - hide when showing progress or in step 2 (recording) */}
          {!showProgress && step !== 2 && (
            <motion.div 
              className="form-content card"
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: formVisible ? 1 : 0,
                y: 0
              }}
              transition={{ 
                duration: 0.6,
                ease: "easeOut"
              }}
            >
              <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="form-step"
                >
                  <h3>📝 Title & Tease Your Confession</h3>
                  <p>Write a short teaser — enough to spark curiosity, not tell the whole story. This will be visible to everyone who sees your token.</p>
                  
                  <div className="textarea-container">
                    <textarea
                      className="input textarea"
                      placeholder=""
                      value={formData.textContent}
                      onChange={handleTextChange}
                      onFocus={handleTextareaFocus}
                      rows={5}
                      maxLength={250}
                    />
                    <div className="character-count">
                      {formData.textContent.length}/250
                    </div>
                  </div>
                  
                  <div className="writing-tips">
                    <h4>💡 Tips:</h4>
                    <ul>
                      <li><strong>Capture the main idea in a sentence or two</strong></li>
                      <li><strong>Hold back the details</strong> — mystery drives donations</li>
                      <li><strong>Keep it under 250 characters</strong></li>
                      <li><strong>Be authentic</strong> — real stories connect</li>
                    </ul>
                  </div>
                  
                  <div className="step-actions">
                    {isFocused && (
                      <motion.button
                        className="btn btn-secondary cancel-focus-btn"
                        onClick={handleCancelFocus}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                      >
                        <X size={16} />
                        Cancel
                      </motion.button>
                    )}
                    <motion.button
                      className="btn btn-primary"
                      disabled={!canProceedToStep(2)}
                      onClick={() => {
                        // Activate focused mode if not already active
                        if (!isFocused) {
                          setIsFocused(true);
                        }
                        handleStepTransition(2);
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Continue to Recording
                    </motion.button>
                  </div>
                </motion.div>
              )}


              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="form-step"
                >
                  <h3>💝 Choose 3 Charities to Support</h3>
                  <p>Select exactly 3 charities that will receive donations from your token sales.</p>
                  
                  <NGOSelector
                    ngos={ngos}
                    selectedIds={formData.selectedNGOs}
                    onSelectionChange={handleNGOSelection}
                    maxSelection={3}
                  />
                  
                  <div className="terms-agreement">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-custom"></span>
                      <span className="checkbox-text">
                        I confirm that this confession is truthful and I agree to mint it as a token 
                        to support the selected charities.
                      </span>
                    </label>
                  </div>
                  
                  <div className="step-actions">
                    <motion.button
                      className="btn btn-secondary"
                      onClick={() => {
                        handleStepTransition(2);
                        // Keep focused state when going back
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Back
                    </motion.button>
                    <motion.button
                      className="btn btn-success btn-large"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !canSubmit()}
                      whileHover={!isSubmitting && canSubmit() ? { scale: 1.02 } : {}}
                      whileTap={!isSubmitting && canSubmit() ? { scale: 0.98 } : {}}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="spinner" />
                          Creating token...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Submit Confession
                        </>
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              )}

              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ConfessionForm;